import React, {useEffect, useMemo, useState} from "react";
import {useIntl} from "react-intl";
import {Formik} from "formik";
import {isEqual} from "lodash";
import classNames from 'classnames';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {useUsersUIContext} from "../UsersUIContext";
import moment from "moment";
import {Dropdown} from "react-bootstrap";
import {toAbsoluteUrl} from '../../../../../_metronic/_helpers';
import FilterTextField from '../../../_common/components/FilterTextField';
import FilterSelect from '../../../_common/components/FilterSelect';
import ButtonExportToExcel from "../../../_common/components/ButtonExportToExcel";
import FiltersBar from '../../../_common/components/FiltersBar';
import ButtonSmartFilter from '../../../_common/components/ButtonSmartFilter';
import {FilterFromDate, FilterToDate} from "../../../_common/components/FilterDateRange";
import * as actions from '../../_redux/usersActions';
import {generateExcelFile} from "../../../_common/utils/excel";
import {formatPhoneNumberForExcel} from '../../../../../utils';
import {usersHeader} from "../../../_common/constants/exportedFilesMainHeaders";
import {headersUsers} from "../../../_common/constants/exportedFilesHeaders";
import {RequestCancellationManager} from '../../../_common/utils/RequestCancellationManager';
import styles from './styles.module.scss';

const prepareFilter = (queryParams, values) => {
  const {
    name,
    agentID,
    idNumber,
    email,
    phoneNumber,
    fromDate,
    toDate,
    agents,
    delivery,
    fromAge,
    toAge,
    incomeExists,
    affiliateCompanyID,
    tamalID,
    tamalIDStatus,
    active,
    advertisement,
    practiceArea,
  } = values;
  const newQueryParams = {...queryParams};
  const filter = {};
  if (Boolean(name)) {
    filter.name = name;
  } else {
    delete newQueryParams.name;
  }
  if (Boolean(agentID)) {
    filter.agentID = agentID;
  } else {
    delete newQueryParams.agentID;
  }
  if (Boolean(idNumber)) {
    filter.idNumber = idNumber;
  } else {
    delete newQueryParams.idNumber;
  }
  if (Boolean(email)) {
    filter.email = email;
  } else {
    delete newQueryParams.email;
  }
  if (Boolean(phoneNumber)) {
    filter.phoneNumber = phoneNumber;
  } else {
    delete newQueryParams.phoneNumber;
  }
  if (Boolean(fromDate)) {
    filter.fromDate = moment(fromDate).format('YYYY-MM-DD');
  } else {
    delete newQueryParams.fromDate;
  }
  if (Boolean(toDate)) {
    filter.toDate = moment(toDate).add(1, 'days').format('YYYY-MM-DD');
  } else {
    delete newQueryParams.toDate;
  }
  if (Boolean(agents)) {
    filter.agents = agents;
  } else {
    delete newQueryParams.agents;
  }
  if (Boolean(delivery)) {
    filter.delivery = delivery;
  } else {
    delete newQueryParams.delivery;
  }
  if (Boolean(fromAge)) {
    filter.fromAge = fromAge;
  } else {
    delete newQueryParams.fromAge;
  }
  if (Boolean(toAge)) {
    filter.toAge = toAge;
  } else {
    delete newQueryParams.toAge;
  }
  if (Boolean(incomeExists)) {
    filter.incomeExists = incomeExists;
  } else {
    delete newQueryParams.incomeExists;
  }
  if (Boolean(affiliateCompanyID)) {
    filter.affiliateCompanyID = affiliateCompanyID;
  } else {
    delete newQueryParams.affiliateCompanyID;
  }
  if (Boolean(tamalID)) {
    filter.tamalID = tamalID;
  } else {
    delete newQueryParams.tamalID;
  }
  if (Boolean(tamalIDStatus)) {
    filter.tamalIDStatus = tamalIDStatus;
  } else {
    delete newQueryParams.tamalIDStatus;
  }
  if (Boolean(active)) {
    filter.active = active;
  } else {
    delete newQueryParams.active;
  }
  if (Boolean(advertisement)) {
    filter.advertisement = advertisement;
  } else {
    delete newQueryParams.advertisement;
  }
  if (Boolean(practiceArea)) {
    filter.practiceArea = practiceArea;
  } else {
    delete newQueryParams.practiceArea;
  }
  return {...newQueryParams, ...filter};
};

const CustomDDItem = React.forwardRef(
  ({children, style, className, 'aria-labelledby': labeledBy}, ref) => (
    <div
      className={`${className} d-flex align-items-center pr-0 mb-0`}
      style={style}
      ref={ref}
    >
      {children}
    </div>
  )
);

export function UsersFilter() {
  const dispatch = useDispatch();
  const usersUIContext = useUsersUIContext();
  const usersUIProps = useMemo(() => {
    return {
      queryParams: usersUIContext.queryParams,
      setQueryParams: usersUIContext.setQueryParams,
    };
  }, [usersUIContext]);
  const [loading, setLoading] = useState(false);

  const applyFilter = (values) => {
    RequestCancellationManager.stopRequests(actions.getUsersListIDPrefix);

    const newQueryParams = prepareFilter(usersUIProps.queryParams, values);
    if (!isEqual(newQueryParams, usersUIProps.queryParams)) {
      usersUIProps.setQueryParams(newQueryParams);
    }
  };

  const prefix = 'users-filter';
  const sfPrefix = 'users-smart-filter';

  const {formatMessage} = useIntl();

  const qs = new URLSearchParams(window.location.search);
  let name = qs.get("name") || "";
  let agentID = qs.get("agentID") || "";
  let idNumber = qs.get("idNumber") || "";
  let email = qs.get("email") || "";
  let phoneNumber = qs.get("phoneNumber") || "";
  const fromDate = qs.get('fromDate') || '';
  const toDate = Boolean(qs.get('toDate'))
    ? moment(qs.get('toDate')).subtract(1, 'days').toDate()
    : '';
  let agents = qs.get("agents") || "";
  let delivery = qs.get("delivery") || "";
  let fromAge = qs.get("fromAge") || "";
  let toAge = qs.get("toAge") || "";
  let tamalID = qs.get("tamalID") || "";
  let tamalIDStatus = parseInt(qs.get("tamalIDStatus"), 10) || usersUIContext.tamalIDStatusOptions[0].value;
  let active = parseInt(qs.get("active"), 10) || usersUIContext.activeOptions[0].value;
  let advertisement = qs.get("advertisement") || "";
  let incomeExists = qs.get("incomeExists") || "";
  let affiliateCompanyID = qs.get("affiliateCompanyID") || "";
  let practiceArea = qs.get("practiceArea") || "";

  const smartFilterAgentsArray = [
    {id: 'agents-all', agents: '', label: formatMessage({id: `${sfPrefix}-agents-all`, defaultMessage: 'All'})},
    {id: 'agents-true', agents: 'true', label: formatMessage({id: `${sfPrefix}-agents-true`, defaultMessage: 'True'})},
    {id: 'agents-false', agents: 'false', label: formatMessage({id: `${sfPrefix}-agents-false`, defaultMessage: 'False'})},
  ];
  const smartFilterDeliveryArray = [
    {id: 'delivery-all', delivery: '', label: formatMessage({id: `${sfPrefix}-delivery-all`, defaultMessage: 'All'})},
    {id: 'delivery-true', delivery: 'true', label: formatMessage({id: `${sfPrefix}-delivery-true`, defaultMessage: 'True'})},
    {id: 'delivery-false', delivery: 'false', label: formatMessage({id: `${sfPrefix}-delivery-false`, defaultMessage: 'False'})},
  ];
  const smartFilterAgeArray = [
    {id: 'age-all', fromAge: '', toAge: '', label: formatMessage({id: `${sfPrefix}-age-all`, defaultMessage: 'All'})},
    {id: 'age-16-19', fromAge: '16', toAge: '19', label: '16-19'},
    {id: 'age-19-30', fromAge: '19', toAge: '30', label: '19-30'},
    {id: 'age-30-50', fromAge: '30', toAge: '50', label: '30-50'},
    {id: 'age-50-100', fromAge: '50', toAge: '100', label: '50-100'},
  ];
  const smartFilterAdvertisementArray = [
    {id: 'advertisement-all', advertisement: '', label: formatMessage({id: `${sfPrefix}-advertisement-all`, defaultMessage: 'All'})},
    {id: 'advertisement-true', advertisement: 'true', label: formatMessage({id: `${sfPrefix}-advertisement-approved`, defaultMessage: 'Approved'})},
    {id: 'advertisement-false', advertisement: 'false', label: formatMessage({id: `${sfPrefix}-advertisement-not-approved`, defaultMessage: 'Not approved'})},
  ];
  const incomeExistsArray = [
    {value: '1', label: formatMessage({id: `${sfPrefix}-incomeExists-all`, defaultMessage: 'Income exists - all'})},
    {value: '2', label: formatMessage({id: `${sfPrefix}-incomeExists-exists`, defaultMessage: 'Income exists - exists'})},
    {value: '3', label: formatMessage({id: `${sfPrefix}-incomeExists-doesNotExist`, defaultMessage: 'Income exists - doesNotExist',})},
  ];

  const smartFilterByAgents = (setFieldValue, item, handleSubmit) => {
    setFieldValue('agents', `${item.agents}`);
    handleSubmit();
  };
  const smartFilterByDelivery = (setFieldValue, item, handleSubmit) => {
    setFieldValue('delivery', `${item.delivery}`);
    handleSubmit();
  };
  const smartFilterByFromAge = (setFieldValue, item, handleSubmit) => {
    setFieldValue('fromAge', `${item.fromAge}`);
    handleSubmit();
  };
  const smartFilterByToAge = (setFieldValue, item, handleSubmit) => {
    setFieldValue('toAge', `${item.toAge}`);
    handleSubmit();
  };
  const smartFilterByAdvertisement = (setFieldValue, item, handleSubmit) => {
    setFieldValue('advertisement', `${item.advertisement}`);
    handleSubmit();
  };
  const smartFilterByTamalIDStatus = (setFieldValue, item, handleSubmit) => {
    setFieldValue('tamalIDStatus', `${item.value}`);
    handleSubmit();
  };
  const smartFilterByActive = (setFieldValue, item, handleSubmit) => {
    setFieldValue('active', `${item.value}`);
    handleSubmit();
  };

  useEffect(() => {
    dispatch(actions.getPartnersOptions())
    dispatch(actions.getPracticeAreaOptions())
  }, []);
  const partnersOptions = useSelector((state) => state.users.partnersOptions, shallowEqual);
  const practiceAreaOptions = useSelector((state) => state.users.practiceAreaOptions, shallowEqual);

  const exportUsersDataFileFunc = () => {
    setLoading(true);
    dispatch(actions.exportUsersDataFileAction(usersUIProps.queryParams))
      .then(prepareDataForExcel)
      .then((data) => generateExcelFile(data, "Users data.xlsx", usersHeader))
      .catch((error) => {
        console.error(`Error: + ${error}`);
        setLoading(false);
      })
      .finally(() => setLoading(false));
  };
  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      const {
        profile,
        email,
        agentId,
        tamalId,
        receiveNotifications,
        createdAt,
        userAdditionalData,
        lastTransferDate
      } = item;
      const {practiceData: {practiceAreaText}} = userAdditionalData;

      const firstNameValue = Boolean(profile && profile.firstName) ? profile.firstName : '';
      const lastNameValue = Boolean(profile && profile.lastName) ? profile.lastName : '';
      const fullNameValue = (firstNameValue && lastNameValue)
        ? `${firstNameValue} ${lastNameValue}`
        : formatMessage({id: 'users-column-fullName-doesNot-exist', defaultMessage: 'Full name does not exist',});
      const emailValue = Boolean(email) ? email : '';
      const cityValue = Boolean(profile && profile.city) ? profile.city : '';
      const addressValue = Boolean(profile && profile.address) ? profile.address : '';
      const fullAddressValue = `${cityValue} ${addressValue}`;
      const agentIdValue = Boolean(agentId) ? agentId : '0';
      const idNumberValue = Boolean(profile && profile.idNumber) ? profile.idNumber : '';
      const tamalIdValue = Boolean(tamalId) ? tamalId : '';
      const phoneValue = Boolean(profile && profile.phone) ? formatPhoneNumberForExcel(profile.phone) : '';
      const notificationValue = Boolean(receiveNotifications) ? receiveNotifications : '';
      const createdAtValue = Boolean(createdAt) ? moment(createdAt).format('DD-MM-YYYY') : '';
      const practiceAreaTextValue = Boolean(practiceAreaText) ? practiceAreaText : '';
      const lastTransferDateValue = Boolean(lastTransferDate) ? moment(lastTransferDate).format('DD.MM.YYYY') : '';

      const isWithinLastThreeMonths = (lastTransferDate) => {
        if (!lastTransferDate) return false;

        const date = new Date(lastTransferDate);
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

        return date >= threeMonthsAgo;
      }

      return {
        [headersUsers[0]]: index + 1,
        [headersUsers[1]]: fullNameValue,
        [headersUsers[2]]: emailValue,
        [headersUsers[3]]: agentIdValue,
        [headersUsers[4]]: idNumberValue,
        [headersUsers[5]]: tamalIdValue,
        [headersUsers[6]]: fullAddressValue,
        [headersUsers[7]]: phoneValue,
        [headersUsers[8]]: notificationValue,
        [headersUsers[9]]: createdAtValue,
        [headersUsers[10]]: practiceAreaTextValue,
        [headersUsers[11]]: isWithinLastThreeMonths(lastTransferDate) ? 'פעיל' : 'לא פעיל',
        [headersUsers[12]]: lastTransferDateValue,
      }
    });
  };

  const containerClassnames = 'w-200px position-relative my-1 px-3';

  return (
      <Formik
        initialValues={{
          name,
          agentID,
          idNumber,
          email,
          phoneNumber,
          fromDate,
          toDate,
          agents,
          delivery,
          fromAge,
          toAge,
          incomeExists,
          affiliateCompanyID,
          tamalID,
          tamalIDStatus,
          active,
          advertisement,
          practiceArea,
        }}
        onSubmit={(values) => applyFilter(values)}
        enableReinitialize={true}
      >
        {({values, ...formikProps}) => (
            <FiltersBar
              filters={
                <div className={'d-flex flex-wrap justify-content-center align-items-center mb-0 w-100'}>
                  <FilterTextField
                    fieldName="name"
                    placeholder={formatMessage({id: `${prefix}-name`, defaultMessage: "Name"})}
                    value={values.name}
                    containerStyles={containerClassnames}
                    {...formikProps}
                  />
                  <FilterTextField
                    fieldName="agentID"
                    placeholder={formatMessage({id: `${prefix}-agentId`, defaultMessage: "Agent Id"})}
                    value={values.agentID}
                    containerStyles={containerClassnames}
                    {...formikProps}
                  />
                  <FilterTextField
                    fieldName="idNumber"
                    placeholder={formatMessage({id: `${prefix}-idNumber`, defaultMessage: "Id Number"})}
                    value={values.idNumber}
                    containerStyles={containerClassnames}
                    {...formikProps}
                  />
                  <FilterTextField
                    type="email"
                    fieldName="email"
                    placeholder={formatMessage({id: `${prefix}-email`, defaultMessage: "Email"})}
                    value={values.email}
                    containerStyles={containerClassnames}
                    {...formikProps}
                  />
                  <FilterTextField
                    fieldName="phoneNumber"
                    placeholder={formatMessage({id: `${prefix}-phone`, defaultMessage: "Phone"})}
                    value={values.phoneNumber}
                    containerStyles={containerClassnames}
                    {...formikProps}
                  />
                  <FilterTextField
                    type="number"
                    fieldName="tamalID"
                    placeholder={formatMessage({id: `${prefix}-tamalID`, defaultMessage: "TamalID"})}
                    value={values.tamalID}
                    containerStyles={containerClassnames}
                    {...formikProps}
                  />
                </div>
              }
              buttons={
                <div className="d-flex align-items-center justify-content-between">
                  <ButtonSmartFilter header={formatMessage({id: `${sfPrefix}-header`, defaultMessage: 'Users smart filter'})}>
                    <div className="d-flex w-100 py-2">
                      <FilterFromDate values={values} {...formikProps}/>
                      <FilterToDate values={values} {...formikProps}/>
                    </div>
                    <Dropdown.Item as={CustomDDItem}>
                      <div className="d-flex">
                        <h7 className={styles.filterName}>
                          {formatMessage({id: `${sfPrefix}-agents-header`, defaultMessage: 'Agents'})}
                        </h7>
                        <div className='d-flex flex-wrap'>
                          {smartFilterAgentsArray.map((item) => {
                            return (
                              <div className="users-smart-filter-item">
                                <input
                                  type="radio"
                                  className="hide-checkbox"
                                  id={item.id}
                                  name="agents"
                                  onBlur={formikProps.handleBlur}
                                  checked={values.agents === item.agents}
                                  onChange={() => {
                                    smartFilterByAgents(formikProps.setFieldValue, item, formikProps.handleSubmit)
                                  }}
                                />
                                <label htmlFor={item.id} className="custom-checkbox">
                                  {item.label}
                                </label>
                              </div>
                            )
                          })
                          }
                        </div>
                      </div>
                    </Dropdown.Item>
                    <Dropdown.Item as={CustomDDItem}>
                      <div className="d-flex">
                        <h7 className={styles.filterName}>
                          {formatMessage({id: `${sfPrefix}-delivery-header`, defaultMessage: 'Delivery'})}
                        </h7>
                        <div className='d-flex flex-wrap'>
                          {smartFilterDeliveryArray.map((item) => {
                            return (
                              <div className="users-smart-filter-item">
                                <input
                                  type="radio"
                                  className="hide-checkbox"
                                  id={item.id}
                                  name="delivery"
                                  onBlur={formikProps.handleBlur}
                                  checked={values.delivery === item.delivery}
                                  onChange={() => {
                                    smartFilterByDelivery(formikProps.setFieldValue, item, formikProps.handleSubmit)
                                  }}
                                />
                                <label htmlFor={item.id} className="custom-checkbox">
                                  {item.label}
                                </label>
                              </div>
                            )
                          })
                          }
                        </div>
                      </div>
                    </Dropdown.Item>
                    <Dropdown.Item as={CustomDDItem}>
                      <div className="d-flex">
                        <h7 className={styles.filterName}>
                          {formatMessage({id: `${sfPrefix}-tamalIDStatus-header`, defaultMessage: 'Tamal ID Status'})}
                        </h7>
                        <div className='d-flex flex-wrap'>
                          {usersUIContext.tamalIDStatusOptions.map((item, index) => {
                            const id = `tamalIDStatus-${index}`;
                            return (
                              <div className="users-smart-filter-item-age">
                                <input
                                  type="radio"
                                  className="hide-checkbox"
                                  id={id}
                                  name="tamalIDStatus"
                                  value={item.value}
                                  onBlur={formikProps.handleBlur}
                                  checked={values.tamalIDStatus === item.value}
                                  onChange={() => {
                                    smartFilterByTamalIDStatus(formikProps.setFieldValue, item, formikProps.handleSubmit)
                                  }}
                                />
                                <label htmlFor={id} className="custom-checkbox">
                                  {item.label}
                                </label>
                              </div>
                            )
                          })
                          }
                        </div>
                      </div>
                    </Dropdown.Item>
                    <Dropdown.Item as={CustomDDItem}>
                      <div className="d-flex">
                        <h7 className={styles.filterName}>
                          {formatMessage({id: `${sfPrefix}-active-header`, defaultMessage: 'Active'})}
                        </h7>
                        <div className='d-flex flex-wrap'>
                          {usersUIContext.activeOptions.map((item, index) => {
                            const id = `active-${index}`;
                            return (
                              <div className="users-smart-filter-item-age">
                                <input
                                  type="radio"
                                  className="hide-checkbox"
                                  id={id}
                                  name="active"
                                  value={item.value}
                                  onBlur={formikProps.handleBlur}
                                  checked={values.active === item.value}
                                  onChange={() => {
                                    smartFilterByActive(formikProps.setFieldValue, item, formikProps.handleSubmit)
                                  }}
                                />
                                <label htmlFor={id} className="custom-checkbox">
                                  {item.label}
                                </label>
                              </div>
                            )
                          })
                          }
                        </div>
                      </div>
                    </Dropdown.Item>
                    <Dropdown.Item as={CustomDDItem}>
                      <div className="d-flex">
                        <h7 className={styles.filterName}>
                          {formatMessage({id: `${sfPrefix}-age-header`, defaultMessage: 'Age'})}
                        </h7>
                        <div className='d-flex flex-wrap'>
                          {smartFilterAgeArray.map((item) => {
                            return (
                              <div className="users-smart-filter-item-age">
                                <input
                                  type="checkbox"
                                  className="hide-checkbox"
                                  id={item.id}
                                  onBlur={formikProps.handleBlur}
                                  checked={(values.fromAge === item.fromAge) || (values.toAge === item.toAge)}
                                  onChange={() => {
                                    smartFilterByFromAge(formikProps.setFieldValue, item, formikProps.handleSubmit)
                                    smartFilterByToAge(formikProps.setFieldValue, item, formikProps.handleSubmit)
                                  }}
                                />
                                <label htmlFor={item.id} className="custom-checkbox">
                                  {item.label}
                                </label>
                              </div>
                            )
                          })
                          }
                        </div>
                      </div>
                    </Dropdown.Item>
                    <Dropdown.Item as={CustomDDItem}>
                      <div className="d-flex">
                        <h7 className={styles.filterName}>
                          {formatMessage({id: `${sfPrefix}-advertisement-header`, defaultMessage: 'Advertisement '})}
                        </h7>
                        <div className='d-flex flex-wrap'>
                          {smartFilterAdvertisementArray.map((item) => {
                            return (
                              <div className="users-smart-filter-item">
                                <input
                                  type="radio"
                                  className="hide-checkbox"
                                  id={item.id}
                                  name="advertisement"
                                  onBlur={formikProps.handleBlur}
                                  checked={values.advertisement === item.advertisement}
                                  onChange={() => {
                                    smartFilterByAdvertisement(formikProps.setFieldValue, item, formikProps.handleSubmit)
                                  }}
                                />
                                <label htmlFor={item.id} className="custom-checkbox">
                                  {item.label}
                                </label>
                              </div>
                            )
                          })
                          }
                        </div>
                      </div>
                    </Dropdown.Item>
                    <Dropdown.Item as={CustomDDItem}>
                      <FilterSelect
                        fieldName={'incomeExists'}
                        valueName={values.incomeExists}
                        options={incomeExistsArray}
                        placeholder={formatMessage({id: `${sfPrefix}-incomeExists`, defaultMessage: 'Income',})}
                        containerStyles={styles.affiliateSelector}
                        {...formikProps}
                      />
                    </Dropdown.Item>
                    <Dropdown.Item as={CustomDDItem}>
                      <FilterSelect
                        fieldName={'affiliateCompanyID'}
                        valueName={values.affiliateCompanyID}
                        options={partnersOptions}
                        placeholder={formatMessage({id: 'partners-filter-partners', defaultMessage: 'Partners'})}
                        containerStyles={styles.affiliateSelector}
                        {...formikProps}
                      />
                    </Dropdown.Item>
                    <Dropdown.Item as={CustomDDItem}>
                      <FilterSelect
                        fieldName={'practiceArea'}
                        valueName={values.practiceArea}
                        options={practiceAreaOptions}
                        placeholder={formatMessage({id: `${sfPrefix}-practiceArea`, defaultMessage: 'Practice data'})}
                        containerStyles={styles.affiliateSelector}
                        {...formikProps}
                      />
                    </Dropdown.Item>
                  </ButtonSmartFilter>
                  <ButtonExportToExcel
                    exportFunc={exportUsersDataFileFunc}
                    loading={loading}
                  />
                  <a
                    href='https://system.atzmai.co.il/'
                    target='_blank'
                    className={classNames(styles.linkButton, 'btn btn-sm p-0')}
                  >
                    <img
                      src={toAbsoluteUrl('/media/svg/Add-user_white.svg')}
                      alt="add-user"
                    />
                  </a>
                </div>
              }
            />
        )}
      </Formik>
  );
}
